// Available plans
export enum Plan {
    Public = "public",
    UbpBasic = "basic",
    UbpCore = "core",
    UbpGrowth = "growth-ubp",
    UbpPremiumTrial = "trial",
    EnterpriseGrowth = "growth",
    Enterprise = "enterprise",
    Legacy = "legacy",
    LegacyStarter = "starter",
    LegacySandbox = "sandbox",
    Research = "research",
    UninitalizedWorkspace = null
}

export interface PlanMeta {
    title: string;
    description: string;
    tagline: string;
    icon: string;
    support: string;
    visibleOnPricingTable?: boolean;
}

export interface PlanPrice {
    monthly: number | string;
    annual: number | string;
}

export interface PlanUbp {
    includedCredits: number;
    creditCap: number | "none" | -1;
    pricePerCredit: number | string;
}

export interface PlanLimits {
    sources: number | string;
    transforms: number;
    collaborators: number | string;
    projects: number | string;
    variantsPerImage: number;
    soft: boolean;
    labelers?: number;
    reviewers?: number;
    auditLogQueryRangeDays?: number;
}

export interface PlanTrain {
    quota: number | Record<string, number>;
    used: number;
    pt: boolean;
    modelLicense: number;
    reload?: number;
    accurate?: boolean;
    rollover?: boolean;
}

export interface PlanInference {
    soft: boolean;
    quota: number;
    used: number;
    billing_period: "monthly" | "annual";
    last_billed_on: null;
    replenish_after_billing: boolean;
}

export interface PlanAutoLabel {
    monthly_quota: number;
    monthly_used: number;
    purchased_credits: number;
    purchased_used: number;
}

export interface PlanVideoInference {
    quota: number | string;
    used: number;
}

export interface PlanDeploy {
    limit: number | string;
    mau: number;
}

export interface PlanFeatures extends Record<string, any> {
    creditSystem: boolean;
    allowPrivateProjects: boolean;
    apiUpload: boolean;
    emailUpload: boolean;
    videoProcessing: boolean;
    healthCheck: boolean;
    outsourcedAnnotations: boolean;
    advancedTransforms: boolean;
    exportVersion: boolean;
    cloudMlIntegrations: boolean;
    tfjsInBrowser: string;
    edgeInference: string;
    embedInMobileApps: boolean;
    offlineMode: boolean;
    vpcDeployment: boolean;
    supportLevel: string;
    annotationReview: boolean;
    workspaceInsights: boolean;
    classAttributes: boolean;
    advancedLabelAssist: boolean;
    deviceMonitoring: boolean;
    rbac: boolean;
    highComplexitySmartPoly: boolean;
    modelEvaluation: boolean;
    activeLearning: boolean;
    modelMonitoring: boolean;
    hipaaCompliance: boolean;
    sessionRecording: boolean;
    manageSourceVideo: boolean;
    refreshAnnotationsButton: boolean;
    unlimitedTimeDedicatedDeployments: boolean;
    billSelfHostedDeployments: boolean;
    projectFolders: boolean;
    restrictTopLevelFolderCreation: boolean;
    advancedApiKeys: boolean;
    useProjectFoldersForWorkflows: boolean;
    disableAutoInstantModelTraining: boolean;
    hideUpgradeCTA?: boolean;
    folderBilling: boolean;
    restrictTopLevelFolderCreation?: boolean;

    bucketMirroring: boolean;
    unrestrictedEmailProxy: boolean;
    deviceManagerV2?: boolean;
    deviceAio?: boolean;
    folderPermissions?: boolean;
    workflowBlockControls?: boolean;
    workflowsEditorV2Default?: boolean;
    creditUsageDashboard?: boolean;
    visionEvents?: boolean;
    auditLogSaveEntries?: boolean;
    auditLogs?: boolean;
    aiSdkWorkflowAssistant?: boolean;
}

export interface PlanDefinition {
    plan: Plan | string | null;
    billing?: boolean;
    pro: boolean;
    restricted: boolean;

    meta?: PlanMeta;

    price: PlanPrice;

    ubp: PlanUbp;

    limit: PlanLimits;

    train: PlanTrain;

    soft: boolean;

    inference: PlanInference;

    video_inference: PlanVideoInference;

    auto_label: PlanAutoLabel;

    tfjs: PlanDeploy;

    oak: PlanDeploy;

    tensorrt: PlanDeploy;

    coreml: PlanDeploy;

    features: PlanFeatures;

    // For threshold billing
    threshold?: number;
    rates?: { sources: number; transforms: number };

    total?: number;
    images?: { sources: number; transforms: number };

    workspace: {
        isSetup: boolean;
        team: boolean;
        plan: Plan | string | null;
        planRecommendation?: string;
        intendedUse?: string;
    };

    trial?: {
        isTrial: boolean;
        startDate: any;
        endDate: any;
        endOfTrialHandled?: boolean;
        downgraded?: any;
        expired?: any;
    };

    // Optional extra fields for specific plans
    research?: { selectedPlan: any; startDate: Date };
    promotions?: any;
}
